import React, { useEffect, useRef, useState } from 'react';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { FOOTER_MARGIN } from '@atlassian/jira-dashboard-common/src/constants.tsx';
import { ColumnPlaceholderContextConsumer } from '@atlassian/jira-dashboard-internal-common/src/ui/dashboard-content/layout/main.tsx';
import AddGadgetImg from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/empty/components/generic-empty-state';
import { useIntl } from '@atlassian/jira-intl';
import { ADD_GADGET_LINK } from '../header-editable/add-gadget-button/constants.tsx';
import { AddGadgetButton } from '../header-editable/add-gadget-button/main.tsx';
import messages from './messages';
import type { GadgetPlaceholderProps } from './types';

const GadgetPlaceholder = ({ isDraggingOver, columnIndex }: GadgetPlaceholderProps) => {
	const { formatMessage } = useIntl();
	const heightRef = useRef<HTMLDivElement>(null);
	const [height, setHeight] = useState<string | undefined>();

	// We want the placeholder to take up the rest of the remaining visible space
	// in the column, which we can't really do with CSS, so we'll calculate what
	// the height should be with JavaScript.
	useEffect(() => {
		if (!heightRef.current) return;

		const distanceFromTopOfWindow = heightRef.current.getBoundingClientRect().top;
		setHeight(`calc(100vh - ${distanceFromTopOfWindow}px - ${FOOTER_MARGIN}px)`);
	}, [heightRef]);

	return (
		// According to https://hello.atlassian.net/wiki/spaces/AF/pages/2630143294/CSS+and+Styling+Components
		// this is the correct way to handle styles that can't be known ahead of time
		// eslint-disable-next-line jira/react/no-style-attribute
		<div ref={heightRef} style={{ height }}>
			<Flex
				direction="column"
				justifyContent="center"
				alignItems="center"
				xcss={[wrapperStyles, isDraggingOver ? draggingStyles : notDraggingStyles]}
			>
				<Box xcss={imageStyles}>
					<AddGadgetImg alt="" />
				</Box>
				<Box xcss={contentStyles}>
					{formatMessage(messages.description)}
					<AddGadgetButton buttonType={ADD_GADGET_LINK} columnIndex={columnIndex} />
				</Box>
			</Flex>
		</div>
	);
};

export const ColumnPlaceholder = ({ customHeight }: { customHeight?: string }) => (
	<ColumnPlaceholderContextConsumer>
		{(placeholderProps) =>
			placeholderProps && <GadgetPlaceholder {...placeholderProps} customHeight={customHeight} />
		}
	</ColumnPlaceholderContextConsumer>
);

const wrapperStyles = xcss({
	height: '100%',
	minHeight: '400px',
	marginBottom: 'space.200',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	border: `dashed ${token('color.border.bold', colors.N60)} 1px`,
	borderRadius: 'border.radius.050',
	transition: 'background-color 0.2s ease',
});
const notDraggingStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	background: token('color.background.input', colors.N0),
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
const draggingStyles = xcss({ background: token('color.background.input.hovered', colors.N20) });
const contentStyles = xcss({
	whiteSpace: 'pre-wrap',
	textAlign: 'center',
	marginTop: 'space.200',
	color: 'color.text.subtlest',
});
const imageStyles = xcss({ width: '112px', marginInline: 'auto' });
